import { DeleteOutlined, EditOutlined, ExportOutlined } from '@ant-design/icons';
import { Button, Col, notification, Popconfirm, Row, Tag } from 'antd';
import dayjs from 'dayjs';
import { useWindowSize } from 'hooks';
import { useDeleteRelationGroupMutation } from 'services';
import { Link, useNavigate } from 'react-router-dom';

import ArticleRelationShownModal from './article-relation-show-modal';

/**
 * @param {Object} params
 * @param {Object} params.filters - Current filter state from useFilters, used to set filteredValue and sortOrder on columns
 */
function useColumn({ filters = {} } = {}) {
  const [deleteRelationGroup] = useDeleteRelationGroupMutation();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  /** Sütun sabitleme yalnızca geniş ekranlarda uygulanır; küçük ekranlarda tüm sütunlar akar */
  const fixedLeft = width > 575 ? 'left' : null;
  const fixedRight = width > 575 ? 'right' : null;

  /** @param {string} field - Returns Ant Design sortOrder for the given field based on current filters */
  const getSortOrder = (field) => {
    if (filters._sort !== field) return null;
    return filters._sortDir === 'asc' ? 'ascend' : 'descend';
  };
  const handleDelete = async (id) => {
    const { error } = await deleteRelationGroup({ uid: id });
    if (error) {
      return notification.error({
        message: 'İlişki Grubu Silme Hatası',
        description: error.data.message,
      });
    }

    notification.success({
      message: 'İlişki Grubu Başarıyla Silindi',
    });
  };

  const column = [
    {
      title: 'Madde Adı',
      dataIndex: 'title',
      width: 250,
      fixed: fixedLeft,
      sorter: true,
      sortOrder: getSortOrder('title'),
      render: (text, record) => (
        <div className="gx-d-flex gx-align-items-center" style={{ gap: 6 }}>
          <Link to={`/madde-iliskilendirme-yonetimi/duzenle/${record.id}`}>
            {text}
          </Link>
          {record.resourceUrl && (
            <a href={record.resourceUrl} target="_blank" rel="noopener noreferrer">
              <ExportOutlined style={{ fontSize: 14, color: '#1890ff' }} />
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Ansiklopedi',
      dataIndex: 'type',
      width: 140,
      fixed: fixedLeft,
      sorter: true,
      sortOrder: getSortOrder('type'),
      filters: [
        { text: 'DİA', value: 'dia' },
        { text: 'TİA', value: 'tia' },
        { text: 'TEMATİK', value: 'tematik' },
      ],
      filteredValue: filters['type[]'] ? [].concat(filters['type[]']) : null,
      render: (text) => <Tag>{text.toUpperCase()}</Tag>,
    },
    {
      title: 'Madde Grubu',
      dataIndex: 'articleGroup',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('articleGroup'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Madde Türü',
      dataIndex: 'articleType',
      width: 140,
      sorter: true,
      sortOrder: getSortOrder('articleType'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Sorumlu İlim Heyeti',
      dataIndex: 'committee',
      width: 180,
      sorter: true,
      sortOrder: getSortOrder('committee'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Takdim Cümlesi',
      dataIndex: 'lead',
      width: 180,
      sorter: true,
      sortOrder: getSortOrder('lead'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Künye',
      dataIndex: 'kunya',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('kunya'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Doğum Ölüm Tarihi',
      dataIndex: 'lifespan',
      width: 170,
      sorter: true,
      sortOrder: getSortOrder('lifespan'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Terk mi',
      dataIndex: 'isAbandoned',
      width: 120,
      filters: [
        { text: 'Terk', value: true },
        { text: 'Terk Değil', value: false },
      ],
      filterMultiple: false,
      filteredValue: filters.isAbandoned != null ? [filters.isAbandoned] : null,
      render: (value) => value == null ? '-' : (
        <Tag color="blue">{value ? 'Terk' : 'Terk Değil'}</Tag>
      ),
    },
    {
      title: 'Taslak mı',
      dataIndex: 'isDraft',
      width: 130,
      filters: [
        { text: 'Taslak', value: true },
        { text: 'Taslak Değil', value: false },
      ],
      filterMultiple: false,
      filteredValue: filters.isDraft != null ? [filters.isDraft] : null,
      render: (value) => value == null ? '-' : (
        <Tag color="orange">{value ? 'Taslak' : 'Taslak Değil'}</Tag>
      ),
    },
    {
      title: 'İptal mi',
      dataIndex: 'isCancelled',
      width: 120,
      filters: [
        { text: 'İptal', value: true },
        { text: 'İptal Değil', value: false },
      ],
      filterMultiple: false,
      filteredValue: filters.isCancelled != null ? [filters.isCancelled] : null,
      render: (value) => value == null ? '-' : (
        <Tag color="red">{value ? 'İptal' : 'İptal Değil'}</Tag>
      ),
    },
    {
      title: 'İlişkili Maddeler',
      dataIndex: 'relationsCount',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('relationsCount'),
      filters: [
        { text: 'İlişkisi Olmayanlar', value: true },
      ],
      filterMultiple: false,
      filteredValue: filters.hasNoRelation != null ? [filters.hasNoRelation] : null,
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: 'İç Kontrol Durumu',
      dataIndex: 'hasChecked',
      width: 160,
      filters: [
        { text: 'Yapıldı', value: 'true' },
        { text: 'Yapılmadı', value: 'false' },
      ],
      filterMultiple: false,
      filteredValue: filters.hasChecked != null ? [filters.hasChecked] : null,
      render: (value) => (
        <Tag color={value ? 'green' : 'red'}>
          {value ? 'Yapıldı' : 'Yapılmadı'}
        </Tag>
      ),
    },
    {
      title: 'Eklenme Tarihi',
      dataIndex: 'createdAt',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('createdAt'),
      render: (text) => (text ? dayjs(text).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      fixed: fixedRight,
      render: (_, record) => {
        return (
          <Row>
            <Col>
              <ArticleRelationShownModal uid={record.id} />
            </Col>
            <Col>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/madde-iliskilendirme-yonetimi/duzenle/${record.id}`)}
              />
            </Col>
            <Col>
              <Popconfirm
                title="İlişki Grubunu Silmek İstediğinize Emin Misiniz?"
                okText="Evet"
                cancelText="Hayır"
                okButtonProps={{
                  onClick: () => handleDelete(record.id),
                }}
              >
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Col>
          </Row>
        );
      },
    },
  ];
  return column;
}

export default useColumn;
